import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from '../../../util/reactIntl';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';
// LocalStorage-based history removed; using Sharetribe protectedData instead
import { searchAllContent } from '../../../ducks/globalSearch.duck';
import { listPreviousSearchByUser, upsertPreviousSearch } from '../../../util/api';
import Icons from '../../Icons/Icons';
import css from './GlobalSearch.module.css';

// Automatically chain-load all result pages once the first page is loaded
// Disable to meet requirement: load 10 initially, then lazy-load next 10 on scroll
const AUTO_CHAIN_LOADS = false;

const buildResultLabel = r => {
  const type = r?.type;
  const a = r?.item?.attributes || {};
  return a.title || a.name || a.slug || type || '';
};

const buildResultPath = (routeConfiguration, r) => {
  const type = r?.type;
  const id = r?.item?.id;
  const a = r?.item?.attributes || {};
  const slug = a.slug;
  switch (type) {
    case 'blogs':
      if (id && slug)
        return createResourceLocatorString('SingleBlogPage', routeConfiguration, { id, slug }, {});
      return '/blogs';
    case 'tutorials':
      if (slug)
        return createResourceLocatorString('VideoPlayerPage', routeConfiguration, { slug }, {});
      return '/gallery';
    case 'events':
      if (a.title)
        return createResourceLocatorString(
          'EventDetailPage',
          routeConfiguration,
          { title: a.title },
          {}
        );
      return '/events';
    case 'topics':
    case 'topicDetails':
      if (id) {
        const topicId = a?.topic?.data?.id;
        const query = topicId ? `?topicId=${topicId}` : '';
        return `/resources/${id}${query}`;
      }
      if (slug) return `/written-guide/${slug}`;
      return '/resources';
    case 'theBasics':
      return '/resources';
    case 'symptoms':
      return '/resources/menopause#symptoms';
    case 'recipes':
      return '/resources';
    case 'products':
      if (slug) return `/products-and-treatments/${slug}`;
      return '/products-and-treatments';
    default:
      return '/search-content';
  }
};

const PAGE_SIZE = 10;
const MAX_HISTORY_ITEMS = 3; // Show up to 3 history results
const MAX_INITIAL_ITEMS = 10; // Combined history + results initially

const SearchPanel = ({ value, onSelect }) => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const dispatch = useDispatch();
  const results = useSelector(state => state.globalSearch?.results || []);
  const inProgress = useSelector(state => state.globalSearch?.inProgress);
  const hasMore = useSelector(state => state.globalSearch?.hasMore);
  const currentUserId = useSelector(
    state => state.user?.currentUser?.id?.uuid || state.user?.currentUser?.id
  );
  const isAuthenticated = !!currentUserId;
  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const [pagesLoaded, setPagesLoaded] = useState(1);
  const [prevResults, setPrevResults] = useState([]);
  const [showNoResults, setShowNoResults] = useState(false);
  const noResultsTimerRef = useRef(null);
  // No local visibleCount; server pages the results

  // No client-side Greek normalization or toast statistics

  const items = useMemo(() => {
    const trimmed = (value || '').trim();
    const minLen = 2;
    const composed = [];
    const norm = s => (s || '').toLowerCase();

    // No-typing case: show up to 3 previously clicked results from DB
    if (!trimmed && isAuthenticated) {
      if (prevResults.length > 0) {
        composed.push({ kind: 'section', id: 'GlobalSearch.lastSearches' });
        composed.push(
          ...prevResults.slice(0, MAX_HISTORY_ITEMS).map(r => ({
            kind: 'history',
            label: r.label || r.title || r.name || r.slug || r.type,
            path: r.path,
          }))
        );
      }
    }

    if (trimmed.length >= minLen) {
      // Determine visible results count (combined with up to 3 history matches initially)
      const combinedCap = MAX_INITIAL_ITEMS + (Math.max(1, pagesLoaded) - 1) * PAGE_SIZE;
      // Prepare base mapped items
      const filteredResults = isAuthenticated ? results : results.filter(r => r?.type === 'blogs');

      // Build history matches (up to 3) first
      const historyMatches = isAuthenticated
        ? (prevResults || [])
            .filter(h =>
              norm(h?.label || h?.title || h?.name || h?.slug || '').includes(norm(trimmed))
            )
            .slice(0, MAX_HISTORY_ITEMS)
            .map(h => ({
              kind: 'history',
              label: h.label || h.title || h.name || h.slug || h.type,
              path: h.path,
            }))
        : [];

      const remainingCapForResults = Math.max(0, combinedCap - historyMatches.length);

      const baseMapped = filteredResults.slice(0, remainingCapForResults).map(r => ({
        kind: 'result',
        label: buildResultLabel(r),
        path: buildResultPath(routeConfiguration, r),
        type: r?.type,
        slug: r?.item?.attributes?.slug,
        itemId: r?.item?.id,
      }));

      // Detect duplicate labels across different types and append [TYPE]
      if (baseMapped.length > 1) {
        const typeBadge = t => {
          switch (t) {
            case 'blogs':
              return 'BLOG';
            case 'tutorials':
              return 'VIDEO';
            case 'events':
              return 'EVENT';
            case 'topics':
            case 'topicDetails':
              return 'TOPIC';
            case 'theBasics':
              return 'BASICS';
            case 'symptoms':
              return 'SYMPTOM';
            case 'recipes':
              return 'RECIPE';
            case 'products':
              return 'PRODUCT';
            default:
              return (t || '').toString().toUpperCase();
          }
        };

        const norm = s => (s || '').trim().toLowerCase();
        const labelTypes = new Map();
        baseMapped.forEach(it => {
          const key = norm(it.label);
          const set = labelTypes.get(key) || new Set();
          if (it.type) set.add(it.type);
          labelTypes.set(key, set);
        });
        const duplicateKeys = new Set(
          Array.from(labelTypes.entries())
            .filter(([, set]) => set.size > 1)
            .map(([k]) => k)
        );
        if (duplicateKeys.size > 0) {
          baseMapped.forEach(it => {
            if (duplicateKeys.has(norm(it.label)) && it.type) {
              it.label = `${it.label} [${typeBadge(it.type)}]`;
            }
          });
        }
      }

      if (historyMatches.length > 0) {
        composed.push({ kind: 'section', id: 'GlobalSearch.lastSearches' });
        composed.push(...historyMatches);
      }

      if (baseMapped.length > 0) {
        composed.push({ kind: 'section', id: 'GlobalSearch.results' });
        composed.push(...baseMapped);
      } else if (showNoResults) {
        // Show explicit no-results state when search yields nothing
        composed.push({ kind: 'section', id: 'GlobalSearch.noResults' });
      }

      if (isAuthenticated && !AUTO_CHAIN_LOADS && hasMore) composed.push({ kind: 'sentinel' });
    }
    return composed;
  }, [
    value,
    results,
    routeConfiguration,
    hasMore,
    intl,
    pagesLoaded,
    prevResults,
    inProgress,
    showNoResults,
  ]);

  useEffect(() => {
    const trimmed = (value || '').trim();

    if (noResultsTimerRef.current) {
      clearTimeout(noResultsTimerRef.current);
      noResultsTimerRef.current = null;
    }

    if (trimmed.length < 2 || inProgress) {
      setShowNoResults(false);
    } else if (results.length === 0) {
      noResultsTimerRef.current = setTimeout(() => {
        setShowNoResults(true);
        noResultsTimerRef.current = null;
      }, 400);
    } else {
      setShowNoResults(false);
    }

    return () => {
      if (noResultsTimerRef.current) {
        clearTimeout(noResultsTimerRef.current);
        noResultsTimerRef.current = null;
      }
    };
  }, [value, results, inProgress]);

  // Reset paging when search term changes
  useEffect(() => {
    setPagesLoaded(1);
  }, [value]);

  // Chain-load all pages immediately when enabled
  const chainingRef = useRef(false);
  const lastChainTermRef = useRef('');
  useEffect(() => {
    if (!isAuthenticated || !AUTO_CHAIN_LOADS) return;
    const trimmed = (value || '').trim();
    if (trimmed.length < 2) return;
    if (!hasMore || inProgress) return;

    // Prevent duplicate chains for the same term
    const searchTerm = trimmed;
    if (chainingRef.current && lastChainTermRef.current === searchTerm) return;
    chainingRef.current = true;
    lastChainTermRef.current = searchTerm;

    let cancelled = false;
    (async () => {
      try {
        let offset = results.length;
        const rawLocale =
          (typeof window !== 'undefined' && window.localStorage.getItem('locale')) || 'el';
        const locale = rawLocale === 'el' ? 'el-GR' : rawLocale;
        // Safety to avoid infinite loops
        let loops = 0;
        const MAX_LOOPS = 200;

        while (!cancelled && lastChainTermRef.current === searchTerm) {
          const resp = await dispatch(
            searchAllContent({
              q: searchTerm,
              defaultLocale: locale,
              limit: PAGE_SIZE,
              offset,
              append: true,
            })
          );
          const got = Array.isArray(resp?.results) ? resp.results.length : 0;
          const more = !!resp?.hasMore;
          if (!got) break;
          offset += got;
          setPagesLoaded(n => n + 1);
          if (!more) break;
          loops += 1;
          if (loops >= MAX_LOOPS) break;
        }
      } finally {
        chainingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [value, hasMore, inProgress, results.length, dispatch, isAuthenticated]);

  // Fallback: lazy load on scroll when auto-chaining is disabled
  useEffect(() => {
    if (!isAuthenticated || AUTO_CHAIN_LOADS) return;
    const trimmed = (value || '').trim();
    if (!sentinelRef.current || !containerRef.current) return;
    if (trimmed.length < 2) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && hasMore && !inProgress && !loadingRef.current) {
            loadingRef.current = true;
            const rawLocale =
              (typeof window !== 'undefined' && window.localStorage.getItem('locale')) || 'el';
            const locale = rawLocale === 'el' ? 'el-GR' : rawLocale;
            const normalizedTerm = trimmed;
            dispatch(
              searchAllContent({
                q: normalizedTerm,
                defaultLocale: locale,
                limit: PAGE_SIZE,
                offset: results.length,
                append: true,
              })
            )
              .then(() => setPagesLoaded(n => n + 1))
              .finally(() => setTimeout(() => (loadingRef.current = false), 50));
          }
        });
      },
      { root: containerRef.current, rootMargin: '0px', threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [value, hasMore, inProgress, results.length, dispatch]);

  // Fetch previous results from DB when user is known
  useEffect(() => {
    const fetchPrev = async () => {
      try {
        const rawLocale =
          (typeof window !== 'undefined' && window.localStorage.getItem('locale')) || 'el';
        const language = rawLocale === 'el' ? 'el-GR' : rawLocale;
        if (!currentUserId) return;
        const res = await listPreviousSearchByUser({
          userId: String(currentUserId),
          language,
          limit: 10,
        });
        setPrevResults(res?.results || []);
      } catch (e) {
        // silent fail
      }
    };
    if (isAuthenticated) {
      fetchPrev();
    } else {
      setPrevResults([]);
    }
  }, [currentUserId, isAuthenticated]);

  if (!inProgress && items.length === 0) return null;

  return (
    <div className={css.headerPanel} data-header-search-panel="true">
      <div className={css.headerPanelInner} ref={containerRef}>
        {items.map((it, idx) =>
          it.kind === 'section' ? (
            <div key={`section-${idx}`} className={css.sectionHeader} role="presentation">
              {intl.formatMessage({ id: it.id })}
            </div>
          ) : it.kind === 'sentinel' ? (
            <div
              key={`sentinel-${idx}`}
              ref={sentinelRef}
              className={css.sentinel}
              aria-hidden="true"
            />
          ) : (
            <div
              key={`${it.kind}-${idx}-${it.label}`}
              className={css.listItem}
              role="option"
              onMouseDown={async e => {
                e.preventDefault();
                if (it.path) {
                  if (it.kind === 'result') {
                    // Persist on server for DB-based previous results
                    try {
                      const rawLocale =
                        (typeof window !== 'undefined' && window.localStorage.getItem('locale')) ||
                        'el';
                      const language = rawLocale === 'el' ? 'el-GR' : rawLocale;
                      if (currentUserId) {
                        await upsertPreviousSearch({
                          userId: String(currentUserId),
                          type: it.type || 'content',
                          path: it.path,
                          language,
                          itemId: it.itemId,
                          slug: it.slug,
                          title: it.label,
                          label: it.label,
                        });
                      }
                    } catch (_) {}
                  }
                  window.location.href = it.path;
                } else {
                  onSelect?.(it);
                }
              }}
            >
              <div
                className={classNames(
                  css.listItemIcon,
                  it.kind === 'history' || it.isFromHistory ? css.listItemIconHistory : null,
                  it.kind === 'result' && !it.isFromHistory ? css.listItemIconSearch : null
                )}
              >
                <Icons
                  name={
                    it.kind === 'loadMore'
                      ? 'downArrow'
                      : it.kind === 'history' || it.isFromHistory
                      ? 'historySmall'
                      : 'search'
                  }
                />
              </div>
              <div className={css.itemText}>{it.label}</div>
            </div>
          )
        )}
        {inProgress ? (
          <div className={css.sectionHeader} role="presentation">
            {intl.formatMessage({ id: 'GlobalSearch.searching' })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchPanel;
